// Interactive Google Maps view for the Homes page.
// Renders one marker per listing pulled from Layer 2 (which surfaces
// lat / lng on the normalized listing shape). Marker click opens an
// info window with a mini-card; clicking the card navigates to the
// detail page.
//
// LAZY LOAD
//   Uses the existing loadGoogleMaps() utility that AddressAutocomplete
//   already calls. Maps JS API loads once per session — multiple
//   instances of HomesMap on the same page reuse the same global
//   window.google.maps.
//
// FALLBACKS
//   · No API key → loadGoogleMaps rejects; we render a "map
//     unavailable" message + a static-iframe fallback so the borrower
//     can still see roughly where listings sit.
//   · Listings with null lat/lng → silently skipped (marker can't be
//     placed; the listing still surfaces in the card grid which is
//     side-by-side with the map).

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';

function fmt$(n) {
  if (n == null) return '';
  return '$' + Number(n).toLocaleString();
}

export default function HomesMap({ listings = [] }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const navigate = useNavigate();
  const [loadError, setLoadError] = useState(null);
  // mapReady flips true once loadGoogleMaps() resolves and the Map
  // instance is constructed. Drives the marker-placement effect's
  // deps so it re-fires when init completes — without this, the
  // marker effect runs once on mount (mapRef.current is still null
  // because init is async), bails, and never re-runs because
  // `listings` hasn't changed. mapRef.current = new Map(...) is a
  // ref mutation, not state, so it doesn't trigger a re-render on
  // its own. The mapReady state IS the re-render trigger.
  const [mapReady, setMapReady] = useState(false);

  // Load the Maps JS API + initialize the map instance once.
  useEffect(() => {
    let mounted = true;
    loadGoogleMaps()
      .then(() => {
        if (!mounted || !containerRef.current) return;
        const google = window.google;
        if (!google?.maps) { setLoadError('maps_unavailable'); return; }
        // Center on Houston (HAR feed's center of gravity); the
        // listings bounds-fit below overrides this immediately when
        // there are markers.
        mapRef.current = new google.maps.Map(containerRef.current, {
          center: { lat: 29.7604, lng: -95.3698 }, // Houston
          zoom:   10,
          // Doorly-flavored map styling — soft cool palette, hide
          // POIs that clutter (gas stations, ATMs, etc.) so listing
          // pins are the focal point.
          styles: [
            { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
            { featureType: 'transit',      stylers: [{ visibility: 'off' }] },
            { featureType: 'road',  elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
          ],
          mapTypeControl:    false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        infoWindowRef.current = new google.maps.InfoWindow();
        // Trigger a re-render so the marker effect (which depends on
        // mapReady) re-fires with mapRef.current now set.
        setMapReady(true);
      })
      .catch((err) => {
        console.error('[HomesMap] loadGoogleMaps failed:', err.message);
        setLoadError(err.message || 'load_failed');
      });
    return () => { mounted = false; };
  }, []);

  // Refresh markers whenever the listings prop changes — borrower
  // filters / sort / saved-tab toggle all change the visible set. Also
  // re-fires when mapReady flips true (post-init), which is the
  // initial-mount case where listings arrived before the async map
  // constructor completed.
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    const google = window.google;
    // Clear prior markers.
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (infoWindowRef.current) infoWindowRef.current.close();

    // Null-aware coordinate filter. The naive Number.isFinite(Number(x))
    // check collapses on Number(null) === 0 → isFinite(0) === true,
    // which would silently place markers at (0, 0) — Null Island, off
    // the West African coast — when a listing's coords are missing.
    // The explicit != null gate catches null AND undefined before the
    // numeric coercion. Same Number(null) === 0 collapse pattern that
    // bit SmartEstimate's down-payment guard earlier.
    const valid = (listings || []).filter(
      (l) => l.lat != null && l.lng != null
          && Number.isFinite(Number(l.lat)) && Number.isFinite(Number(l.lng))
    );
    if (valid.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    valid.forEach((listing) => {
      const pos = { lat: Number(listing.lat), lng: Number(listing.lng) };
      bounds.extend(pos);
      const marker = new google.maps.Marker({
        position: pos,
        map:      mapRef.current,
        title:    listing.address?.full || listing.address?.street || '',
        // Brand-color pin via SVG (Doorly blue).
        icon: {
          path:           google.maps.SymbolPath.CIRCLE,
          scale:          9,
          fillColor:      '#3a6ad9',
          fillOpacity:    1,
          strokeColor:    '#ffffff',
          strokeWeight:   2,
        },
      });
      marker.addListener('click', () => {
        if (!infoWindowRef.current) return;
        const photoUrl = listing.photos && listing.photos[0]
          ? listing.photos[0]
          : null;
        const detailHref = `/homes/${listing.listingId || listing.id}`;
        const subtitle = [
          listing.beds != null ? `${listing.beds}bd` : null,
          listing.baths != null ? `${listing.baths}ba` : null,
          listing.sqft ? `${Number(listing.sqft).toLocaleString()} sqft` : null,
        ].filter(Boolean).join(' · ');
        // InfoWindow body — inline HTML string (the Google Maps API
        // doesn't take React children for InfoWindow content). Keep
        // styles inline so we don't depend on Tailwind escaping
        // through the iframe-style render context.
        infoWindowRef.current.setContent(`
          <div style="min-width: 200px; max-width: 240px; font-family: system-ui;">
            ${photoUrl
              ? `<img src="${photoUrl}" alt="" style="width:100%;height:120px;object-fit:cover;border-radius:6px;margin-bottom:8px;" />`
              : ''}
            <div style="font-size:15px;font-weight:700;color:#111827;line-height:1.2;">${fmt$(listing.price)}</div>
            <div style="font-size:12px;color:#374151;margin-top:2px;">${(listing.address?.street || '').replace(/</g, '&lt;')}</div>
            <div style="font-size:11px;color:#9ca3af;margin-top:1px;">${subtitle}</div>
            <a id="hmap-detail-link" href="${detailHref}" style="display:inline-block;margin-top:8px;font-size:12px;font-weight:600;color:#3a6ad9;text-decoration:none;">View details →</a>
          </div>
        `);
        infoWindowRef.current.open({
          anchor:  marker,
          map:     mapRef.current,
          shouldFocus: false,
        });
        // Intercept the link click to use SPA navigation instead of
        // a full page reload. Google's InfoWindow renders the HTML
        // into the DOM AFTER open(); attach the handler in a
        // microtask so the element exists.
        setTimeout(() => {
          const link = document.getElementById('hmap-detail-link');
          if (link) {
            link.addEventListener('click', (e) => {
              e.preventDefault();
              navigate(detailHref);
            }, { once: true });
          }
        }, 0);
      });
      markersRef.current.push(marker);
    });

    // Fit the map to the markers. Single-marker case avoids
    // fitBounds' over-zoom artifact.
    if (valid.length === 1) {
      mapRef.current.setCenter({ lat: Number(valid[0].lat), lng: Number(valid[0].lng) });
      mapRef.current.setZoom(13);
    } else {
      mapRef.current.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
    }
  }, [listings, navigate, mapReady]);

  if (loadError) {
    return (
      <div style={{
        flex: 1, borderRadius: 12, overflow: 'hidden',
        border: '1px solid #e5e7eb', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#f9fafb', color: '#9ca3af', fontSize: 13,
        textAlign: 'center', padding: 24,
      }}>
        Map unavailable — set REACT_APP_GOOGLE_MAPS_API_KEY to enable the interactive map.
      </div>
    );
  }

  return (
    // homes-map-pane class — under 640px the parent (homes-map-view)
    // stacks column-flex so this pane needs an explicit min-height
    // to avoid collapsing to nothing inside the flex.
    <div className="homes-map-pane" style={{ flex: 1, borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb', position: 'relative', minHeight: 420 }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <div style={{
        position: 'absolute', bottom: 12, left: 12,
        background: 'rgba(255,255,255,0.92)', padding: '6px 12px',
        borderRadius: 8, fontSize: 12, color: '#6b7280',
        border: '1px solid #e5e7eb',
      }}>
        📍 {(listings || []).filter(l => l.lat != null && l.lng != null).length} homes in this view
      </div>
    </div>
  );
}
